import React, { useEffect } from "react";
import { FieldArray, Field, ErrorMessage, useFormikContext } from "formik";
import ReactSelect from "react-select";
import AsteriskLabel from "../../../Common/AsteriskLabel";
import NumberInputBox from "../../../CustomComponents/AllCustomeInput/NumberInputBox";
import NumberInputBoxDuration from "../../../CustomComponents/AllCustomeInput/NumberInputBoxDuration";
import CloudStorage from "../../../../CloudStorage/CloudStorage";
import { nomorethenhandread } from "../../../Common/FewCommonMethods";
import { Tooltip } from "react-tooltip";
import { penaltytips } from "../../../Common/Tooltips/tooltip";

const duretiontypeoptions = [
  { value: 2, label: "Days" },
  { value: 3, label: "Weeks" },
  { value: 4, label: "Months" },
];

const MilestoneWatcher = ({ push }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    // Add initial milestone if needed
    if (
      values.payment_type == "2" &&
      Array.isArray(values.milestones) &&
      values.milestones.filter(m => m.status !== 3).length === 0
    ) {
      push({
        title: "",
        duration: "",
        amount: "",
        penalty: "",
        duration_type: "",
        status: 1
      });
    }
  }, [values.payment_type, values.milestones, push]);

  return null;
};

const MilestonesFieldArray = ({
  formik,
  penaltyInputMode,
  togglePenaltyMode,
  handleProficiencyChange,
  handlePenaltyInputChange,
  handleDeadlinetypeChange,
  penaltylisting = [],
}) => {
  // Get only active milestones (status !== 3) for display
  const activeMilestones = formik.values.milestones.filter(milestone => milestone.status !== 3);

  return (
    <>
      <FieldArray name="milestones">
        {({ push, remove }) => (
          <div>
            <MilestoneWatcher push={push} />

            {activeMilestones.length > 0 &&
              activeMilestones.map((milestone, displayIndex) => {
                // Find the original index in the formik.values.milestones array
                const originalIndex = formik.values.milestones.findIndex(
                  m => m === milestone
                );

                return (
                  <div className="milestone" key={originalIndex}>
                    <div className="row">
                      <div className="col-md-8 form-group" style={{ marginBottom: "12px" }}>
                        <label>
                          {displayIndex + 1}. Milestone <AsteriskLabel />
                        </label>
                        <Field
                          name={`milestones.${originalIndex}.title`}
                          placeholder="Enter milestone name"
                        />
                        <ErrorMessage
                          name={`milestones.${originalIndex}.title`}
                          component="div"
                          className="error"
                        />
                      </div>

                      <div className="col-md-4 form-group">
                        <div className="form-group1">
                          <label>
                            Duration type <AsteriskLabel />
                          </label>
                          <ReactSelect
                            options={duretiontypeoptions}
                            placeholder="Select"
                            value={
                              duretiontypeoptions.find(
                                (option) =>
                                  option.value == formik.values.milestones[originalIndex].duration_type
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              handleDeadlinetypeChange(selectedOption, originalIndex, formik.setFieldValue)
                            }
                          />
                          <ErrorMessage
                            name={`milestones.${originalIndex}.duration_type`}
                            component="div"
                            className="error"
                          />
                        </div>
                      </div>

                      <div className="col-md-4 form-group">
                        <NumberInputBoxDuration
                          title={`Duration in ${formik.values.milestones[originalIndex].duration_type == 2
                            ? "day"
                            : formik.values.milestones[originalIndex].duration_type == 3
                              ? "weeks"
                              : formik.values.milestones[originalIndex].duration_type == 4
                                ? "months"
                                : ""
                            }`}
                          type="number"
                          name={`milestones.${originalIndex}.duration`}
                          placeholder="Enter Duration"
                        />
                      </div>

                      <div className="col-md-4 form-group">
                        <label>
                          Amount <AsteriskLabel />
                        </label>
                        <NumberInputBox
                          type="number"
                          name={`milestones.${originalIndex}.amount`}
                          placeholder="Enter amount"
                        />
                      </div>

                      <div className="col-md-4 form-group">
                        <div className="form-group1">
                          <label>
                            Penalty in % <AsteriskLabel /> &nbsp;
                            <img
                              data-tooltip-id="my-tooltip-1"
                              src={`${CloudStorage.cloudurl}/Assets/Images/common/info-circle.svg`}
                              alt=""
                            />
                          </label>

                          {penaltyInputMode[originalIndex] ? (
                            <input
                              type="text"
                              value={formik.values.milestones[originalIndex].penalty}
                              onChange={(event) =>
                                handlePenaltyInputChange(event, originalIndex, formik.setFieldValue)
                              }
                              placeholder="Enter penalty"
                              onKeyDown={nomorethenhandread}
                            />
                          ) : (
                            <ReactSelect
                              options={
                                Array.isArray(penaltylisting)
                                  ? penaltylisting?.map((penalty) => ({
                                    value: penalty.id,
                                    label: penalty.title,
                                  }))
                                  : []
                              }
                              placeholder="Select"
                              value={(() => {
                                const milestone = formik.values.milestones[originalIndex];

                                // 1️⃣ Case: penalty_id exists → show matching option
                                const match = Array.isArray(penaltylisting) && penaltylisting.find(
                                  (p) => p.id == milestone.penalty_id
                                );
                                if (match) return { value: match.id, label: match.title };

                                // 2️⃣ Case: Only penalty text exists (edit mode)
                                if (milestone.penalty) {
                                  return { value: null, label: milestone.penalty };
                                }

                                return null;
                              })()}
                              onChange={(selectedOption) =>
                                handleProficiencyChange(selectedOption, originalIndex, formik.setFieldValue)
                              }
                            />
                          )}

                          <ErrorMessage
                            name={`milestones.${originalIndex}.penalty`}
                            component="div"
                            className="error"
                          />

                          <button
                            type="button"
                            onClick={() => togglePenaltyMode(originalIndex)}
                            className="buttons border-button mt-1"
                          >
                            {penaltyInputMode[originalIndex] ? "Use Select" : "Use Input"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {activeMilestones.length > 1 && (
                      <button
                        type="button"
                        className="btn remove-mile"
                        onClick={() => {
                          // Mark as deleted but keep in array for backend submission
                          formik.setFieldValue(`milestones.${originalIndex}.status`, 3);
                        }}
                      >
                        <img
                          src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/delete-icon.svg`}
                          alt=""
                        />
                      </button>
                    )}
                  </div>
                );
              })}

            <button
              className="add-more-btn btn buttons background-button"
              type="button"
              onClick={() =>
                push({
                  title: "",
                  duration: "",
                  amount: "",
                  penalty: "",
                  duration_type: "",
                  status: 1
                })
              }
            >
              Add more milestones
            </button>

            {/* Debug: Show hidden milestones count (optional) */}
            {formik.values.milestones.filter(m => m.status === 3).length > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                {formik.values.milestones.filter(m => m.status === 3).length} milestone(s) marked for deletion
              </div>
            )}

          </div>
        )}
      </FieldArray>
      <Tooltip
        id="my-tooltip-1"
        place="bottom"
        content={penaltytips}
      />
    </  >
  );
};

export default MilestonesFieldArray;